import React, { useEffect, useCallback, useState, useRef } from 'react'
import { Redirect, Route, useLocation } from 'react-router-dom'
import moment from 'moment'
import { ROUTES } from '../Utils/constants'

import { useDispatch, useSelector } from 'react-redux'
import { useErrorBoundary } from 'use-error-boundary'
import ErrorHandler from '../Components/ErrorHandler'
import { useLogout } from 'Hooks/useLogout'
import Loading from 'Components/Loading'
import LoginForm from 'Views/Login/Components/LoginForm'
import { Modal } from '@mui/material'
import { openLoginModal, authenticateUser } from 'redux/actions/loginActions'
import FloatingCustomerDisplayButton from 'Views/CustomerDisplay/components/FloatingCustomerDisplayButton/FloatingCustomerDisplayButton'
import { useInactivity } from 'Views/CustomerDisplay/hook/useInactivity'

const AppRoutes = ({
  component: Component,
  path,
  exact,
  isPrivate,
  ...rest
}) => {
  const { logout } = useLogout()
  const dispatch = useDispatch()
  const location = useLocation()

  const { ErrorBoundary, didCatch, error } = useErrorBoundary()

  const [loading, setLoading] = useState(true)
  const loginDay = useSelector(state => state.login?.loginAt)
  const token = useSelector(state => state.login?.user?.CustomerInfo?.LoginCode)
  const { isLoginModal } = useSelector(state => state.login)
  const cargando = useSelector(state => state.login.loading)
  const errorLogin = useSelector(state => state.login.error)
  const company = useSelector(state => state.company)
  const { isActiveCustomerDisplay } = useSelector(
    state => state.customerDisplay
  )
  const [tenantValue, setTenantValue] = useState('')

  const [showPassword, setShowPassword] = useState(false)

  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')

  const channelRef = useRef(null)
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel('customer_display')
  }
  // Tracks current sleep mode to avoid stale closures inside event listeners
  const isSleepModeRef = useRef(false)

  // Only listen to Customer Display activity when NOT on the Customer Display route
  const shouldListenToCustomerDisplay = !location.pathname.includes(
    ROUTES.customerDisplay
  )
  const isInactive = useInactivity(
    3 * 60 * 1000, // 3 minutes
    shouldListenToCustomerDisplay ? channelRef.current : null
  )

  const showFloatingButton =
    !location.pathname.includes(ROUTES.customerDisplay) &&
    location.pathname !== ROUTES.login

  const toggleVisibility = () => {
    setShowPassword(!showPassword)
  }

  const checkSessionTime = useCallback(async () => {
    const day = moment().day()
    if (day !== loginDay) {
      await logout()
    }
    setLoading(false)
  }, [])

  // Automatic inactivity: notify CD and keep ref in sync
  useEffect(() => {
    if (isActiveCustomerDisplay && channelRef.current) {
      isSleepModeRef.current = isInactive
      channelRef.current.postMessage({ isSleepMode: isInactive })
    }
  }, [isInactive, isActiveCustomerDisplay])

  // Wake up on any activity: main screen events OR CD userActivity.
  // Needed for manual sleep (isInactive stays false so the effect above never fires).
  // isSleepModeRef is also synced from channel messages sent by FloatingButton.
  useEffect(() => {
    const channel = channelRef.current

    const wakeUp = () => {
      if (isActiveCustomerDisplay && isSleepModeRef.current) {
        isSleepModeRef.current = false
        channel.postMessage({ isSleepMode: false })
      }
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, wakeUp))

    const handleChannelMessage = event => {
      // Sync ref when FloatingButton manually toggles sleep mode
      if (event.data.isSleepMode !== undefined) {
        isSleepModeRef.current = event.data.isSleepMode
      }
      if (event.data.userActivity) wakeUp()
    }
    channel.addEventListener('message', handleChannelMessage)

    return () => {
      events.forEach(e => window.removeEventListener(e, wakeUp))
      channel.removeEventListener('message', handleChannelMessage)
    }
  }, [isActiveCustomerDisplay])

  useEffect(() => {
    loading && checkSessionTime()
    window.addEventListener('click', checkSessionTime)
    return () => {
      window.removeEventListener('click', checkSessionTime)
    }
  }, [loading])

  if (didCatch) return <ErrorHandler error={error.message} />
  if (loading) return <Loading full />

  const submitLogin = async e => {
    e.preventDefault()
    const response = await dispatch(
      authenticateUser(email, password, tenantValue, company)
    )
    if (response) {
      dispatch(openLoginModal(false))
    }
  }

  return (
    <ErrorBoundary>
      <Modal
        open={isLoginModal}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 99999,
        }}>
        <form
          onSubmit={submitLogin}
          className="login-form-expired"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 60,
            backgroundColor: '#FFF',
            borderRadius: 5,
          }}>
          {' '}
          <h2 style={{ marginBottom: 10 }}>
            Sesión expirada, introduce tus credenciales.
          </h2>
          <LoginForm
            setTenantValue={setTenantValue}
            tenantValue={tenantValue}
            setEmail={setEmail}
            email={email}
            showPassword={showPassword}
            setPassword={setPassword}
            password={password}
            toggleVisibility={toggleVisibility}
            cargando={cargando}
            error={errorLogin}
            isLoginTokenExpired
          />
        </form>
      </Modal>
      {showFloatingButton && <FloatingCustomerDisplayButton />}

      <Route
        path={path}
        exact={exact}
        render={props => {
          if (isPrivate && token !== 200) {
            return <Redirect to={{ pathname: ROUTES.login }} />
          } else {
            if (path === ROUTES.login && token === 200) {
              return <Redirect to={{ pathname: ROUTES.home }} />
            }
            return <Component {...props} />
          }
        }}
        {...rest}
      />
    </ErrorBoundary>
  )
}

export default AppRoutes
